import React from 'react'
import { AppProps } from '../../App'
import { Input } from '../../components/Input';
import { UserModel } from '../../utils/interfaces';
import { deleteFile, getFileContent, updateItem, uploadFile } from '../../api/API';
import { languages, MAX_IMAGE_SIZE, storageKeys, storages, tables } from '../../utils/constants';
import { blobToBase64, splitByUpperCase, useAsyncEffect, useEventListener } from '../../utils/functions';
import DropDown from '../../components/DropDown';
import { useTranslation } from 'react-i18next';
import { BoldHeader3, Title } from '../../components/General';
import { BiSolidImage } from 'react-icons/bi';
import imageCompression from 'browser-image-compression';
import AlertWrapper from '../../components/AlertWrapper';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';

const General = (props: AppProps) => {

  const { t, i18n } = useTranslation();
  const [userImage, setUserImage] = React.useState('');
  const [newName, setNewName] = React.useState(props.user!.name);
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = React.useState(false);
  const [showLanguagesDropdown, setShowLanguagesDropdown] = React.useState(false);
  const [showDeletePhotoAlert, setShowDeletePhotoAlert] = React.useState(false);

  useAsyncEffect(async () => {
    if (props.user?.picture) {
      const data = (await getFileContent(storages.userFiles, props.user.picture)).blob;
      if (data) {
        const base64 = await blobToBase64(data);
        setUserImage(base64 as string);
      }
    } else {
      setUserImage('');
    }
  }, [props.user?.picture]);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('#languages-dropdown') && !target.closest('.languages-dropdown-button')) {
      setShowLanguagesDropdown(false);
    }
    if (!target.closest('.delete-photo-alert') && !target.closest('.delete-photo-button')) {
      setShowDeletePhotoAlert(false);
    }
  });

  const saveUser = async () => {
    const newUser: UserModel = { 
      ...props.user!, 
      name: newName 
    };
    props.setUser(newUser);
    setIsEditingName(false);
    await updateItem(tables.users, newUser.id, { name: newName });
  }

  const onImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    let finalFile = file;
    if (file.size > MAX_IMAGE_SIZE) {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
      };
      finalFile = await imageCompression(file, options);
    }
    const fileName = `${props.user?.id2}.${finalFile.name.split('.').pop()}`;
    await uploadFile(storages.userFiles, fileName, finalFile);
    const newUser = { ...props.user!, picture: fileName };
    props.setUser(newUser);
    await updateItem(tables.users, newUser.id, { picture: fileName });
    // update image
    const base64 = await blobToBase64(finalFile);
    setUserImage(base64 as string);
  }

  const deletePhoto = async () => {
    setIsDeletingPhoto(true);
    const fileName = props.user?.picture;
    if (!fileName) return;
    await deleteFile(storages.userFiles, fileName);
    const newUser = { ...props.user!, picture: '' };
    await updateItem(tables.users, newUser.id, { picture: '' });
    props.setUser(newUser);
    setUserImage('');
    setIsDeletingPhoto(false);
    setShowDeletePhotoAlert(false);
  }

  return (
    <>
      <Title>{splitByUpperCase(t('settings.general.title'))}</Title>
      <div className='mt-8 max-w-3xl'>
        <div className='flex justify-between items-start'>
          <div onClick={() => document.getElementById('hidden-image-input')?.click()} className='animate cursor-pointer flex items-center justify-center w-32 h-32 bg-gray-100 text-gray-300 hover:bg-gray-200 hover:text-gray-400 rounded-full overflow-hidden'>
            {userImage ? <img className='w-full h-full object-cover' src={userImage} alt='user' /> : <BiSolidImage className='text-4xl' />}
          </div>
          {props.user?.picture && <button onClick={() => setShowDeletePhotoAlert(true)} className='delete-photo-button text-xs p-2 px-4 rounded-lg bg-gray-100'>{t('settings.general.delete-photo')}</button>}
        </div>
      </div>
      {/* name */}
      <div className='mt-8 max-w-3xl'>
        <div className='flex items-center justify-between'>
          <BoldHeader3>{t('general.name')}</BoldHeader3>
          <div onClick={() => setIsEditingName(!isEditingName)} className='text-sm md:text-base underline cursor-pointer'>{isEditingName ? t('general.cancel') : t('general.edit')}</div>
        </div>
        {isEditingName ? <>
          <Input extraclass='mt-2' autoFocus textSize='small' value={newName} onChange={(e) => setNewName(e.target.value)} />
          <button onClick={saveUser} disabled={!newName || newName === props.user?.name} className='mt-2 text-sm p-1.5 px-6 rounded-lg bg-primary disabled:bg-gray-300 text-white'>{t('general.save')}</button>
        </>
          : <div className='mt-2 text-sm md:text-base'>{props.user?.name}</div>}
      </div>
      {/* email */}
      <div className='mt-8 max-w-3xl'>
        <BoldHeader3>{t('Email')}</BoldHeader3>  
        <div className='mt-2 text-sm md:text-base'>{props.user?.email}</div>
      </div>
      <div className='border-b my-8' />
      {/* language */}
      <div className='max-w-3xl'>
        <BoldHeader3>{t('general.language')}</BoldHeader3>
        <div className='max-w-[320px] relative'>
          <Input extraclass='languages-dropdown-button mt-2 cursor-pointer' onClick={() => setShowLanguagesDropdown(!showLanguagesDropdown)} textSize='small' readOnly value={languages[i18n.language as keyof typeof languages]} />
          {showLanguagesDropdown && <DropDown 
            id='languages-dropdown'
            direction='bottom'
            maxHeight={'400px'}
            options={Object.keys(languages).map(key => languages[key as keyof typeof languages])}
            onSelect={(value) => {
              const key = Object.keys(languages).find(key => languages[key as keyof typeof languages] === value)!;
              i18n.changeLanguage(key);
              localStorage.setItem(storageKeys.language, key);
              setShowLanguagesDropdown(false);
            }}
            buttonStyle={{ textAlign: 'start', padding: '.5rem 1rem' }}
          />}
        </div>
      </div>
      <div className='border-b my-8' />

      {/* hidden image input */}
      <input type='file' accept='image/*' style={{ display: 'none' }} id='hidden-image-input' onChange={onImageSelect} />

      {/* delete image alert */}
      {showDeletePhotoAlert && <AlertWrapper>
        <Alert
            extraclass='delete-photo-alert'
            title={t('settings.general.delete-photo')}
            maxWidth={400}
            onClose={() => setShowDeletePhotoAlert(false)}
            body={<div className='mx-1'>
              <div>{t('settings.general.delete-photo-message')}</div>
            </div>}
            bottom={<div className='flex'>
              <button onClick={deletePhoto} disabled={isDeletingPhoto} className='disabled:bg-gray-300 flex items-center justify-center bg-red-400 text-white text-sm p-2 px-8 rounded-lg'>{isDeletingPhoto ? <Loading /> : t('general.delete')}</button>
            </div>}
          />  
      </AlertWrapper>}
    </>
  )
}

export default General